import { ChannelMessageEntityExtendedClient } from './ChannelMessageEntityExtended';

/**
 * Minimal shape of an Issue Status lookup row — what the ticket-state rules need to know.
 * Kept structural so callers can pass the bizapps IssueStatus entity, a cached lookup, or a
 * lightweight RunView row without coupling this package to @mj-biz-apps types.
 */
export interface TicketStatusLike {
    IsTerminal?: boolean | null;
    Name?: string | null;
}

/** Minimal shape of a message row for the escalation-signal checks (entity or raw RunView row). */
export interface TicketMessageLike {
    ApprovalStatus?: string | null;
}

/**
 * Ticket display/decision state derived from the Issue's status and its linked Channel Messages.
 *
 * Follows the MJ client-entity-extension pattern (MJQueryEntityExtended.IsApproved/IsComposable):
 * derived business state is NAMED and lives on the entity layer, and every UI surface consumes
 * the same definition instead of re-deriving it per component. There is no Izzy client subclass
 * of the bizapps Issue entity to hang getters on (Issues come from @mj-biz-apps), and the rules
 * span two entities anyway — so they live here as static helpers, usable from Angular components,
 * IzzyLib server code, or anything else that loads this package.
 */
export class TicketState {

    /**
     * Closed-out: the ticket is in a terminal status (Closed / Won't Fix) or Resolved.
     * Resolved isn't flagged IsTerminal in bizapps-issues but is treated as closed-out
     * everywhere in Izzy (queue "open" filter, follow-up linking, escalation display).
     * @param status the resolved IssueStatus lookup (null when unknown)
     * @param fallbackStatusName the Issue row's denormalized Status name, for when the lookup missed
     */
    public static IsClosedOut(status: TicketStatusLike | null | undefined, fallbackStatusName?: string | null): boolean {
        if (status?.IsTerminal) return true;
        return (status?.Name || fallbackStatusName || '') === 'Resolved';
    }

    /** Whether one message carries the escalation signal (ApprovalStatus 'Escalated'). */
    public static IsEscalatedMessage(message: TicketMessageLike): boolean {
        return message.ApprovalStatus === 'Escalated';
    }

    /** Whether any linked message carries the escalation signal. */
    public static HasEscalatedMessage(messages: ReadonlyArray<TicketMessageLike>): boolean {
        return messages.some(m => TicketState.IsEscalatedMessage(m));
    }

    /**
     * A ticket IS escalated when a linked message sits in ApprovalStatus 'Escalated' — the same
     * signal the Messages screen badges — AND the ticket is still open. Resolution supersedes
     * escalation: a Resolved/terminal ticket is never shown as escalated, even when its escalated
     * message was never individually dispositioned.
     */
    public static IsTicketEscalated(
        messages: ReadonlyArray<TicketMessageLike>,
        status: TicketStatusLike | null | undefined,
        fallbackStatusName?: string | null
    ): boolean {
        if (TicketState.IsClosedOut(status, fallbackStatusName)) return false;
        return TicketState.HasEscalatedMessage(messages);
    }

    /**
     * Which message carries a ticket-level escalation: the pending AI reply when there is one
     * (that's what the agent is bouncing to a human), else the newest ESCALATABLE message.
     * Null when the ticket has no message that may carry the flag — escalation rides on a
     * message, and an Approved response is never a valid carrier (product rule 2026-08-31:
     * flagging a sent reply as Escalated made it masquerade as an undecided draft in the
     * Messages queue, and re-approving it used to double-send). A ticket whose replies are
     * all Approved therefore cannot be escalated; surfaces hide the affordance on null.
     * Selection policy lives here (not in a component) so every surface that offers
     * "Escalate ticket" targets the same message.
     */
    public static PickEscalationTarget(
        messages: ReadonlyArray<ChannelMessageEntityExtendedClient>
    ): ChannelMessageEntityExtendedClient | null {
        const pending = messages.find(m => m.ApprovalStatus === 'Pending' && !!m.GeneratedReplyContent);
        if (pending) return pending;
        for (let i = messages.length - 1; i >= 0; i--) {
            if (messages[i].IsEscalatable) return messages[i];
        }
        return null;
    }
}
